
import React, { useState, useEffect } from 'react';
import { ScheduleService } from '@/services/classService';
import { ClassSchedule } from '@/types/classTypes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScheduleForm } from './ScheduleForm';
import { toast } from 'sonner';

export interface EditScheduleDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  schedule: ClassSchedule;
  onScheduleUpdated: () => Promise<void>;
}

const EditScheduleDialog: React.FC<EditScheduleDialogProps> = ({
  open,
  onOpenChange,
  schedule,
  onScheduleUpdated,
}) => {
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  // These are not used but required by ScheduleForm props
  const [selectedClassId, setSelectedClassId] = useState(schedule.class_id || '');

  // Initialize form values from schedule
  useEffect(() => {
    if (schedule) {
      // Convert weekday number to string for the form
      setDayOfWeek(schedule.weekday !== undefined ? String(schedule.weekday) : '');
      setStartTime(schedule.start_time || '');
      setEndTime(schedule.end_time || '');
      setSelectedClassId(schedule.class_id || '');
    }
  }, [schedule]);

  const handleUpdateSchedule = async () => {
    if (!dayOfWeek || !startTime || !endTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setIsUpdating(true);
      
      // Update the schedule
      const scheduleData = {
        weekday: parseInt(dayOfWeek),
        start_time: startTime,
        end_time: endTime,
      };
      
      const updatedSchedule = await ScheduleService.updateSchedule(
        schedule.id,
        scheduleData
      );
      
      if (updatedSchedule) {
        toast.success('Schedule updated successfully');
        await onScheduleUpdated();
        onOpenChange?.(false);
      } else {
        toast.error('Failed to update schedule');
      }
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error('An error occurred while updating the schedule');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Schedule</DialogTitle>
        </DialogHeader>

        <ScheduleForm
          dayOfWeek={dayOfWeek}
          setDayOfWeek={setDayOfWeek}
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          setEndTime={setEndTime}
          selectedClassId={selectedClassId}
          setSelectedClassId={setSelectedClassId}
        />

        <DialogFooter>
          <Button 
            variant="secondary" 
            onClick={() => onOpenChange?.(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateSchedule} 
            disabled={isUpdating}
          >
            {isUpdating ? 'Updating...' : 'Update Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditScheduleDialog;
